/**
 * Tile → workspace navigation (S11): drill-through (mark dblclick), the
 * kebab's "Open query in editor", and "Open execution plan".
 *
 * G10 decision (open-QE-and-capture): the plan workspace tab renders
 * IMPORTED artifacts only, so "Open execution plan" opens a query tab seeded
 * with the tile SQL and enqueues an estimated-plan command through the
 * existing per-tab MCP command queue — a live plan for the tile's current
 * query, no artifact plumbing.
 *
 * Drill SQL is a USER-FACING TEXT ARTIFACT (lands in an editor, not an
 * executor), so cross-filter values inline as engine-escaped literals here —
 * the values-bind-as-params rule governs EXECUTION, which stays main-side.
 */
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useQueryEditorMcpStore } from '../../query-editor/store/queryEditorMcpStore';
import type { ActiveDashboardFilter } from '../types/dashboardRuntime';
import { bindingForField, hasLiveFiltersSlot, injectFiltersSlot } from './crossFilter';
import {
  DASHBOARD_FILTERS_NAME,
  buildFilterPredicate,
  type DriverParam,
} from './parameterBinding';
import { findPlaceholders } from './sqlPlaceholders';
import type { QueryBackedTile } from './tileExecutionService';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

/** The drill artifact: commented provenance header + WHERE where derivable. */
export function drillSqlForTile(
  tile: QueryBackedTile,
  filters: ActiveDashboardFilter[],
  engineId: string,
): string {
  // Unlike execution (binding-gated), a drill on the tile's OWN mark maps the
  // clicked column directly — double-clicking a bar means "show me these
  // rows" even on an unbound tile. buildFilterPredicate still identifier-
  // validates every target.
  const wire = filters.map((f) => {
    const binding = bindingForField(tile, f.dashboardField);
    return {
      targetExpression: binding?.targetExpression ?? f.dashboardField,
      valueType: binding?.valueType ?? f.valueType,
      value: f.value === null ? null : String(f.value),
    };
  });
  // C3-R2 (Codex 8): everything interpolated into `--` header lines must be
  // single-line — a \r or \n (tile titles, clicked string values inside the
  // rendered predicate) would end the comment and turn the rest into live
  // SQL in the opened artifact.
  const oneLine = (text: string) => text.replace(/[\r\n]+/g, ' ');
  const header = [`-- Drill-through from dashboard tile "${oneLine(tile.title)}"`];

  let body = tile.source.sql;
  if (wire.length > 0) {
    // Only claim filtering when the predicate actually lands: the base must
    // carry a LIVE slot (an uninjectable body would otherwise get a
    // "-- Cross-filters:" header above unfiltered SQL — C3 finding 7).
    const base = hasLiveFiltersSlot(body, engineId) ? body : injectFiltersSlot(body, engineId);
    const sink = { literalMode: true, engine: engineId, driverParams: [] as DriverParam[] };
    const predicate = base ? buildFilterPredicate(engineId, wire, sink) : null;
    if (base && predicate?.ok) {
      header.push(`-- Cross-filters: ${oneLine(predicate.text)}`);
      // Replace live occurrences only (back-to-front keeps offsets valid) —
      // slot text inside strings/comments stays untouched.
      body = base;
      const slots = findPlaceholders(base, engineId)
        .filter((p) => p.name === DASHBOARD_FILTERS_NAME)
        .reverse();
      for (const slot of slots) {
        body = body.slice(0, slot.start) + predicate.text + body.slice(slot.end);
      }
    }
  }
  if (/\{\{\s*[\w.-]+\s*\}\}/.test(body)) {
    header.push("-- Note: {{parameter}} tokens are bound by the dashboard's parameter chips");
  }
  return `${header.join('\n')}\n${body}`;
}

function openQueryTab(tile: QueryBackedTile, query: string): string {
  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const lookup = getConnectionById(tile.connectionId);
  return addTab({
    title: `Query-${getNextQueryNumber()}`,
    type: 'query',
    content: { query },
    connectionInfo: {
      connectionId: tile.connectionId,
      connectionName: safeConnectionAlias(lookup.connection, ''),
      serverHost: safeConnectionServer(lookup.connection),
      database: tile.database ?? '',
    },
  });
}

/** Kebab "Open query in editor" — the raw tile SQL, plus active-filter
 * provenance when the tile is currently cross-filtered. */
export function openTileInQueryEditor(
  tile: QueryBackedTile,
  filters: ActiveDashboardFilter[],
  engineId: string,
): string {
  const query = filters.length > 0 ? drillSqlForTile(tile, filters, engineId) : tile.source.sql;
  return openQueryTab(tile, query);
}

/** Mark dblclick drill-through — always the composed drill artifact. */
export function openDrillThrough(
  tile: QueryBackedTile,
  filters: ActiveDashboardFilter[],
  engineId: string,
): string {
  return openQueryTab(tile, drillSqlForTile(tile, filters, engineId));
}

/** Kebab "Open execution plan" (G10) — QE tab + queued estimated-plan run. */
export function openTileExecutionPlan(tile: QueryBackedTile): string {
  const tabId = openQueryTab(tile, tile.source.sql);
  useQueryEditorMcpStore
    .getState()
    .queueCommand(tabId, { type: 'execute_with_plan', planMode: 'estimated' });
  return tabId;
}
